import { useRef, useMemo, useState, useCallback, useEffect } from "react";

export function useSelections<TData>(items: TData[], getIdentity: (item: TData) => string) {
    const getIdentityRef = useRef(getIdentity);
    getIdentityRef.current = getIdentity;

    const [selections, setSelections] = useState<TData[]>([]);

    const currentItemKeys = useMemo(() => new Set(items.map((item) => getIdentityRef.current(item))), [items]);
    const currentSelectionKeys = useMemo(() => new Set(selections.map((item) => getIdentityRef.current(item))), [selections]);

    useEffect(() => {
        const difference = currentSelectionKeys.difference(currentItemKeys);
        if (difference.size > 0) {
            setSelections((prev) => prev.filter((value) => !difference.has(getIdentityRef.current(value))));
        }
    }, [currentItemKeys, currentSelectionKeys]);

    const isAllSelected = useMemo(() => currentItemKeys.difference(currentSelectionKeys).size === 0, [currentItemKeys, currentSelectionKeys]);

    const isSelected = useCallback((item: TData) => currentSelectionKeys.has(getIdentityRef.current(item)), [currentSelectionKeys]);
    const toggleSelection = useCallback(
        (selectedItem: TData) => {
            setSelections((prev) => {
                const itemKey = getIdentityRef.current(selectedItem);
                if (!currentItemKeys.has(itemKey)) {
                    return prev;
                }

                const isSelected = !!prev.find((selection) => getIdentityRef.current(selection) === getIdentityRef.current(selectedItem));
                return isSelected ? prev.filter((value) => getIdentityRef.current(value) !== getIdentityRef.current(selectedItem)) : [...prev, selectedItem];
            });
        },
        [currentItemKeys]
    );

    const toggleSelectAll = useCallback(() => {
        setSelections((prev) => (prev.length >= 0 && prev.length < items.length ? items : []));
    }, [items]);

    const clearSelections = useCallback(() => {
        setSelections([]);
    }, []);

    return {
        isAllSelected,
        isSelected,
        toggleSelection,
        toggleSelectAll,
        clearSelections,
        selections,
    };
}
